import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify';
import { Plus, Search } from 'lucide-react';

import DataTable from '../ui/DataTable';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import TeamForm from './TeamForm';
import TeamMemberService from '../../services/TeamMemberService';

function formatLastSeen(dateStr) {
    if (!dateStr) return 'Never';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function formatBattery(pct) {
    if (pct === null || pct === undefined) return '—';
    return `${pct}%`;
}

function getBatteryColor(battery) {
    if (battery === null || battery === undefined) return '#999';
    if (battery > 50) return '#039855';
    if (battery > 20) return '#f79009';
    return '#d92d20';
}

function TeamMemberList() {

    const [members, setTeamMembers] = useState([]);
    const [open, setOpen] = useState(false);
    const [editMember, setEditMember] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadMembers = async () => {
        try {
            let response = await TeamMemberService.findAll(1, 100);
            if (response && response.status == "200") {
                setTeamMembers(response.data);
                setOpen(false);
            }
        } catch (error) { console.log(error) }
        finally { setLoading(false); }
    }

    useEffect(() => { loadMembers() }, []);

    const headCells = [
        { id: 'fullname', label: 'Name' },
        { id: 'role.role_name', label: 'Role' },
        {
            id: 'activity_status.status',
            label: 'Status',
            render: (row) => <Badge>{row.activity_status?.status || '—'}</Badge>,
        },
        { id: 'mobile', label: 'Contact' },
        { id: 'email', label: 'Email' },
        {
            id: 'batteryPercent',
            label: 'Battery',
            render: (row) => (
                <span style={{ color: getBatteryColor(row.batteryPercent), fontWeight: 600 }}>
                    {formatBattery(row.batteryPercent)}
                </span>
            ),
        },
        {
            id: 'lastSeenAt',
            label: 'Last Seen',
            render: (row) => formatLastSeen(row.lastSeenAt),
        },
        {
            id: '_location',
            label: 'Location',
            render: (row) => (row.lat && row.lng ? `${Number(row.lat).toFixed(4)}, ${Number(row.lng).toFixed(4)}` : '—'),
        },
    ];

    const handleAdd = () => {
        setEditMember(null);
        setOpen(true);
    };

    const handleEdit = (row) => {
        setEditMember(row);
        setOpen(true);
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Delete member "${row.fullname}"?`)) return;
        try {
            await TeamMemberService.delete(row.id);
            toast.success("Member deleted successfully");
            loadMembers();
        } catch (error) {
            toast.error("Failed to delete member");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="relative w-full sm:w-80">
                    <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500" />
                    <input
                        type="search"
                        placeholder="Search members..."
                        className="form-input pl-9"
                    />
                </div>
                <Button icon={Plus} onClick={handleAdd}>Add Member</Button>
            </div>

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={editMember ? "Edit Team Member" : "Create Team Member"}
                footer={null}
            >
                <TeamForm setOpen={setOpen} loadMembers={loadMembers} editData={editMember} />
            </Modal>

            <DataTable
                title="Team Members"
                rows={members}
                headCells={headCells}
                onEdit={handleEdit}
                onDelete={handleDelete}
                loading={loading}
                emptyLabel={loading ? 'Loading team members…' : 'No team members found.'}
            />
        </div>
    )
}

export default TeamMemberList
